import { HideImage, QuestionMark } from '@mui/icons-material'
import { Button, Dialog, DialogContent, Grid, TextField } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { VehicleCheckInTirePressureLabelPhotoDto, vehicleCheckInTirePressureLabelPhotoValidationSchema } from 'dtos'
import { Formik } from 'formik'
import {
  CheckInPhotoBrowseTakeRow,
  CheckInPhotoDialogNavProps,
  CheckInPhotoDialogTitleRow,
  CheckInPhotoPreview,
  CheckInPhotoStackedNavButtons,
  useCheckInPhotoSubmitIntent,
  useLifecycleDocumentPreview
} from './checkInPhotoDialogCommon'

interface TirePressureLabelPhotoDialogProps {
  initialValues: VehicleCheckInTirePressureLabelPhotoDto
  isLoading: boolean
  nav?: CheckInPhotoDialogNavProps
  onClose: () => void
  onDelete: () => void
  onSave: (values: VehicleCheckInTirePressureLabelPhotoDto, options?: { goToNext?: boolean }) => void
  open: boolean
  vehicleGuid: string
}

export default function TirePressureLabelPhotoDialog({
  initialValues,
  isLoading,
  nav,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: TirePressureLabelPhotoDialogProps) {
  const { onFormSubmit, submitClose, submitThenNext } = useCheckInPhotoSubmitIntent(onSave)
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getVehicleCheckInPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleCheckInTirePressureLabelPhotoValidationSchema}
        onSubmit={onFormSubmit}
      >
        {({ errors, handleBlur, handleChange, isSubmitting, setFieldValue, submitForm, touched, values }) => {
          return (
            <form onSubmit={e => void e.preventDefault()}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <CheckInPhotoDialogTitleRow onClose={onClose} title='Tire Pressure Label Photo Details' />

                  <Grid item xs={12}>
                    <CheckInPhotoPreview
                      alt='Tire Pressure Label'
                      file={values.vehicleCheckInTirePressureLabelPhotoFile}
                      placeholderSrc='/2 Tire Pressure Label.jpg'
                      serverSrc={serverSrc}
                    />
                  </Grid>

                  <CheckInPhotoBrowseTakeRow
                    browseInputId='tire-pressure-browse'
                    cameraInputId='tire-pressure-camera'
                    disabled={isSubmitting || isLoading || values.vehicleCheckInIsTirePressureLabelPhotoMissing}
                    onFileChosen={file => setFieldValue('vehicleCheckInTirePressureLabelPhotoFile', file)}
                  />

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={
                        isSubmitting ||
                        isLoading ||
                        values.vehicleCheckInIsTirePressureLabelPhotoMissing ||
                        !Boolean(values.documentGuid)
                      }
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Tire Pressure Label Photo?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='primary'
                      disabled={isSubmitting || isLoading}
                      endIcon={<QuestionMark />}
                      fullWidth
                      onClick={() => {
                        setFieldValue(
                          'vehicleCheckInIsTirePressureLabelPhotoMissing',
                          !values.vehicleCheckInIsTirePressureLabelPhotoMissing
                        )
                      }}
                      variant={values.vehicleCheckInIsTirePressureLabelPhotoMissing ? 'contained' : 'outlined'}
                    >
                      TIRE PRESSURE LABEL MISSING
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      error={Boolean(
                        touched.vehicleCheckInTirePressureLabelMissingNote && errors.vehicleCheckInTirePressureLabelMissingNote
                      )}
                      disabled={isSubmitting || isLoading || !values.vehicleCheckInIsTirePressureLabelPhotoMissing}
                      fullWidth
                      helperText={
                        touched.vehicleCheckInTirePressureLabelMissingNote && errors.vehicleCheckInTirePressureLabelMissingNote
                      }
                      label='Tire Pressure Label Missing Note'
                      name='vehicleCheckInTirePressureLabelMissingNote'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.vehicleCheckInTirePressureLabelMissingNote || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || If the Tire Pressure Label Missing image is pressed, the “Tire Pressure Missing Note” field becomes
                      required. || Pressing Submit will verify that a photo has been uploaded or, if a user pressed “Tire
                      Pressure Label Missing”, that the “Tire Pressure Label Missing Note” field is not blank. || Pressing
                      “Previous” goes to the previous photo without saving. ||
                    </ExplanationAccordion>
                  </Grid>

                  <CheckInPhotoStackedNavButtons
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    nav={nav}
                    submitClose={submitClose}
                    submitForm={submitForm}
                    submitThenNext={submitThenNext}
                  />
                </Grid>
              </DialogContent>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
